import { createTheme, css } from '@mui/material/styles';
import color from 'color';
import { DEFAULT_FONT_FAMILY } from '../constants/constants';
import { BLUE_COLOR, RED_BUTTON_COLOR, TURQUOISE_COLOR, YELLOW_COLOR } from './colors';
const DARK_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR = TURQUOISE_COLOR;
const DARK_THEME_CONTAINED_BUTTON_BORDER_COLOR = color(DARK_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR).darken(0.2).hex();
const DARK_THEME_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR = color(DARK_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR)
    .lighten(0.2)
    .hex();
const DARK_THEME_CONTAINED_BUTTON_HOVER_BACKGROUND_COLOR = color(DARK_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR)
    .lighten(0.3)
    .hex();
const DARK_THEME_OUTLINED_BUTTON_BORDER_COLOR = color(BLUE_COLOR).desaturate(0.5).darken(0.2).hex();
const DARK_THEME_OUTLINED_BUTTON_BORDER_HIGHLIGHT_COLOR = color(DARK_THEME_OUTLINED_BUTTON_BORDER_COLOR)
    .lighten(0.2)
    .hex();
const DARK_THEME_OUTLINED_BUTTON_HOVER_BACKGROUND_COLOR = DARK_THEME_OUTLINED_BUTTON_BORDER_COLOR;
const LIGHT_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR = color(BLUE_COLOR).lighten(0.5).hex();
const LIGHT_THEME_CONTAINED_BUTTON_BORDER_COLOR = color(LIGHT_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR)
    .darken(0.25)
    .hex();
const LIGHT_THEME_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR = color(LIGHT_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR)
    .lighten(0.15)
    .hex();
const LIGHT_THEME_OUTLINED_BUTTON_BORDER_COLOR = LIGHT_THEME_CONTAINED_BUTTON_BORDER_COLOR;
const LIGHT_THEME_OUTLINED_BUTTON_BORDER_HIGHLIGHT_COLOR = LIGHT_THEME_CONTAINED_BUTTON_BORDER_COLOR;
const LIGHT_THEME_OUTLINED_BUTTON_HOVER_BACKGROUND_COLOR = color(LIGHT_THEME_OUTLINED_BUTTON_BORDER_COLOR)
    .alpha(0.3)
    .string();
const SECONDARY_CONTAINED_BUTTON_BACKGROUND_COLOR = RED_BUTTON_COLOR;
const SECONDARY_CONTAINED_BUTTON_BORDER_COLOR = color(SECONDARY_CONTAINED_BUTTON_BACKGROUND_COLOR).darken(0.4).hex();
const SECONDARY_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR = color(SECONDARY_CONTAINED_BUTTON_BACKGROUND_COLOR)
    .lighten(0.4)
    .hex();
const baseThemeOverrides = {
    typography: {
        fontFamily: [DEFAULT_FONT_FAMILY, 'Roboto', 'sans-serif'].join(',')
    },
    // Disable all shadows / "elevation" globally:
    // See https://stackoverflow.com/a/62262181
    shadows: Array(25).fill('none')
};
function createComponentStyles({ primaryButtonColors, secondaryButtonColors, disabledButtonColors }) {
    return {
        components: {
            MuiBadge: {
                styleOverrides: {
                    colorSecondary: {
                        color: 'white',
                        backgroundColor: '#F50157'
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: css `
            border-radius: 6px !important;
            text-transform: none;
            transition: none;
            line-height: 1.5;
            padding: 7px 15px;
            &.Mui-disabled {
              color: ${disabledButtonColors.textColor};
              border: 2px solid ${disabledButtonColors.backgroundColor};
              background-color: ${disabledButtonColors.backgroundColor};
            }
          `,
                    contained: css `
            color: ${primaryButtonColors.containedTextColor};
            border: 2px solid ${primaryButtonColors.containedBorderColor};
            border-top-color: ${primaryButtonColors.containedBorderHighlightColor};
            border-left-color: ${primaryButtonColors.containedBorderHighlightColor};
            background-color: ${primaryButtonColors.containedBackgroundColor};
            &:hover {
              border: 2px solid ${primaryButtonColors.containedBorderHighlightColor};
              background-color: ${primaryButtonColors.containedHoverBackgroundColor} !important;
            }
          `,
                    outlined: css `
            border: 2px solid ${primaryButtonColors.outlinedBorderColor};
            border-top-color: ${primaryButtonColors.outlinedBorderHighlightColor};
            border-left-color: ${primaryButtonColors.outlinedBorderHighlightColor};
            &:hover {
              border: 2px solid ${primaryButtonColors.outlinedBorderColor};
              background-color: ${primaryButtonColors.outlinedHoverBackgroundColor} !important;
            }
          `,
                    containedSecondary: css `
            color: ${secondaryButtonColors.containedTextColor};
            border: 2px solid ${secondaryButtonColors.containedBorderColor};
            border-top-color: ${secondaryButtonColors.containedBorderHighlightColor};
            border-left-color: ${secondaryButtonColors.containedBorderHighlightColor};
            background-color: ${secondaryButtonColors.containedBackgroundColor};
            &:hover {
              background-color: ${secondaryButtonColors.containedHoverBackgroundColor} !important;
              border-color: ${secondaryButtonColors.containedHoverBackgroundColor};
            }
          `
                }
            }
        }
    };
}
export const darkTheme = createTheme({
    ...baseThemeOverrides,
    palette: {
        mode: 'dark',
        primary: {
            main: '#FFFFFF'
        },
        secondary: { main: YELLOW_COLOR }
    },
    ...createComponentStyles({
        primaryButtonColors: {
            containedBackgroundColor: DARK_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR,
            containedTextColor: 'black',
            containedBorderColor: DARK_THEME_CONTAINED_BUTTON_BORDER_COLOR,
            containedBorderHighlightColor: DARK_THEME_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR,
            containedHoverBackgroundColor: DARK_THEME_CONTAINED_BUTTON_HOVER_BACKGROUND_COLOR,
            outlinedBorderColor: DARK_THEME_OUTLINED_BUTTON_BORDER_COLOR,
            outlinedBorderHighlightColor: DARK_THEME_OUTLINED_BUTTON_BORDER_HIGHLIGHT_COLOR,
            outlinedHoverBackgroundColor: DARK_THEME_OUTLINED_BUTTON_HOVER_BACKGROUND_COLOR
        },
        secondaryButtonColors: {
            containedBackgroundColor: SECONDARY_CONTAINED_BUTTON_BACKGROUND_COLOR,
            containedTextColor: 'white',
            containedBorderColor: SECONDARY_CONTAINED_BUTTON_BORDER_COLOR,
            containedBorderHighlightColor: SECONDARY_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR,
            containedHoverBackgroundColor: SECONDARY_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR
        },
        disabledButtonColors: {
            backgroundColor: '#666666',
            textColor: '#999999'
        }
    })
});
export const lightTheme = createTheme({
    ...baseThemeOverrides,
    palette: {
        mode: 'light',
        primary: {
            main: '#000000'
        },
        secondary: {
            main: TURQUOISE_COLOR
        }
    },
    ...createComponentStyles({
        primaryButtonColors: {
            containedBackgroundColor: LIGHT_THEME_CONTAINED_BUTTON_BACKGROUND_COLOR,
            containedTextColor: 'black',
            containedBorderColor: LIGHT_THEME_CONTAINED_BUTTON_BORDER_COLOR,
            containedBorderHighlightColor: LIGHT_THEME_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR,
            containedHoverBackgroundColor: LIGHT_THEME_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR,
            outlinedBorderColor: LIGHT_THEME_OUTLINED_BUTTON_BORDER_COLOR,
            outlinedBorderHighlightColor: LIGHT_THEME_OUTLINED_BUTTON_BORDER_HIGHLIGHT_COLOR,
            outlinedHoverBackgroundColor: LIGHT_THEME_OUTLINED_BUTTON_HOVER_BACKGROUND_COLOR
        },
        secondaryButtonColors: {
            containedBackgroundColor: SECONDARY_CONTAINED_BUTTON_BACKGROUND_COLOR,
            containedTextColor: 'white',
            containedBorderColor: SECONDARY_CONTAINED_BUTTON_BORDER_COLOR,
            containedBorderHighlightColor: SECONDARY_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR,
            containedHoverBackgroundColor: SECONDARY_CONTAINED_BUTTON_BORDER_HIGHLIGHT_COLOR
        },
        disabledButtonColors: {
            backgroundColor: '#666666',
            textColor: '#999999'
        }
    })
});
